<template>
  <div class="chat-interface">
    <!-- 顶部固定 MBTI 区域 -->
    <header class="chat-header">
      <div class="chat-header-main">
      <div class="chat-title">
          {{ displayMbti || '' }}-职业规划
      </div>
      <div class="chat-subtitle">内容由 AI 生成，仅供职业规划参考</div>
      </div>
      <div class="chat-header-actions">
        <span class="action-icon" @click="handleShare" title="转发">
          <img src="@/views/index/assets/img/share.png" alt="转发" />
        </span>
        <span 
          class="action-icon favorite-icon" 
          :class="{ active: isFavorite }"
          @click="handleToggleFavorite"
          :title="isFavorite ? '取消收藏' : '收藏'"
        >
          <img
            :src="isFavorite ? favoriteActiveIcon : favoriteDefaultIcon"
            :alt="isFavorite ? '已收藏' : '收藏'"
          />
        </span>
      </div>

    </header>

    <!-- 中间对话内容，可滚动 -->
    <main class="chat-body" ref="messagesContainer" @scroll="handleMessagesScroll">
      <div
        v-for="(msg, index) in messages"
        :key="msg.id || index"
        :class="['chat-message', getMessageRoleClass(msg)]"
      >
        <!-- 左侧头像（AI） -->
        <div v-if="getMessageRoleClass(msg) === 'assistant'" class="message-avatar">
          <img :src="defaultAvatar" alt="AI头像" />
        </div>

        <div class="chat-message-content">
          <div v-if="getMessageType(msg) === 1" class="chat-bubble">
            <template v-if="getMessageRoleClass(msg) === 'assistant'">
              <template v-if="msg.streaming && !msg.text && !msg.content">
                <div class="message-loading">
                  <span class="loading-dot"></span>
                  <span class="loading-dot"></span>
                  <span class="loading-dot"></span>
                  
                </div>
              </template>
              <template v-else>
                <div
                  class="markdown-content"
                  v-html="getRenderedMarkdown(msg)"
                ></div>
              </template>
            </template>

            <template v-else>
              {{ msg.content || msg.text || '' }}
            </template>
          </div>

          <!-- 图片消息：不使用 chat-bubble（无背景色） -->
          <template v-else-if="getMessageType(msg) === 2">
            <img
              class="msg-image"
              :src="getImageSrc(msg)"
              alt="图片消息"
            />
          </template>

          <template v-else-if="getMessageType(msg) === 3">
            <a
              class="msg-file"
              :href="getFileUrl(msg)"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ getFileName(msg) || '文件' }}
            </a>
          </template>

          <template v-else>
              {{ msg.content || '' }}
          </template>
          <section
            v-show="msg.sender_role === 2 && msg.recommend_jobs"
            class="hot-jobs-wrapper"
          >
            <div class="hot-jobs-grid">
              <MbtiHotJobCard
                v-for="(job, jobIndex) in msg.recommend_jobs"
                :key="job.id || job.job_id || jobIndex"
                :job="job"
                @click="handleHotJobClick(job)"
              />
            </div>
          </section>
        </div>

        <!-- 右侧头像（用户） -->
        <div v-if="getMessageRoleClass(msg) === 'user'" class="message-avatar">
          <img :src="userAvatar" alt="用户头像" />
        </div>
      </div>
    </main>

    <!-- 底部输入区域 -->
    <footer class="chat-footer">

      <!-- 当前 MBTI 标签 + 修改按钮 -->
      <div class="switch-btn">
        <div class="switch-btn-left">
          当前选择MBTI：<span class="mbti-code">{{ displayMbti || '未选择' }}</span>
        </div>
        <button class="switch-btn-btn" type="button" @click="mbtiDialogVisible = true">
          点击修改
        </button>
      </div>
      <!-- 当前简历 + 重新上传 -->
      <div class="switch-btn">
        <div class="switch-btn-left">
          当前简历：<span class="mbti-code">{{ displayResumeFileName }}</span>
        </div>
        <button class="switch-btn-btn" type="button" @click="resumeDialogVisible = true">
          点击重新上传
        </button>
      </div>
      <div class="chat-input-wrapper">
        <!-- 文本输入区 -->
        <textarea
          v-model="inputText"
          class="chat-input"
          rows="3"
          placeholder="发送消息或上传相应附件以获取解析"
          @keyup.enter.exact.prevent="handleSend"
          @paste="handlePasteImage"
        ></textarea>

        <!-- 工具栏 -->
        <div class="chat-toolbar">
          <!-- 上传附件：使用与 UploadContent 相同的上传接口 -->
          <!-- <el-upload
            class="chat-upload"
            action="/api/upload/imageObs"
            name="file"
            :show-file-list="false"
            :before-upload="handleBeforeUpload"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :accept="uploadAccept"
          >
            <button class="upload-btn" type="button">
              <span class="upload-icon">📎</span>
              <span class="upload-text">
                {{ isUploading ? '上传中...' : '上传附件' }}
              </span>
            </button>
          </el-upload> -->

          <div class="toolbar-actions">
            <!-- 语音录制：点击开始 / 结束并转为文字填入输入框 -->
            <button
              class="icon-btn"
              type="button"
              :title="isRecording ? '点击结束语音输入' : '点击开始语音输入'"
              @click="handleVoiceClick"
            >
              <img 
                :src="isRecording ? voiceGif : voiceIcon" 
                alt="语音" 
                class="icon-btn-img" 
              />
            </button>
        <button
          class="btn-send"
              type="button"
              :disabled="!inputText.trim() || isStreaming"
          @click="handleSend"
        >
          发送
        </button>
          </div>
        </div>
      </div>
    </footer>

    <!-- MBTI 修改弹框 -->
    <el-dialog
      title="请选择你的MBTI"
      :visible.sync="mbtiDialogVisible"
      width="960px"
      append-to-body
      :close-on-click-modal="false"
    >
      <MbtiSelectionContent
        :is-sidebar-collapsed="false"
        :is-in-dialog="true"
        :show-header-and-footer="false"
        @select="handleMbtiSelect"
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="mbtiDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleChangeMbti">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 简历重新上传弹框 -->
    <el-dialog
      title="简历上传"
      :visible.sync="resumeDialogVisible"
      width="960px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="resume-dialog"
    >
      <UploadContent
        :selected-mbti="displayMbti"
        :is-in-dialog="true"
        :hide-actions="true"
        @uploaded="handleResumeUploaded"
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleResumeCancel">取 消</el-button>
        <el-button type="primary" :disabled="!tempResumeUrl" @click="handleResumeConfirm">
          确 定
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MbtiHotJobCard from './HotJobCard.vue'
import { favoriteSession, getSessionMessages, getSessionDetail, updateSession } from '@/api/careerPlan'
import { streamChat } from '@/utils/streamChat'
import MbtiSelectionContent from './MbtiSelection.vue'
import UploadContent from './UploadContent.vue'
import favoriteActiveIconUrl from '@/views/hot-jobs/assets/img/favorite.png'
import favoriteDefaultIconUrl from '@/views/index/assets/img/star.png'
import voiceIcon from '@/views/mbti-selection/assets/img/voice.png'
import voiceGif from '@/views/mbti-selection/assets/img/voice.gif'
import aiAvatar from '@/views/mbti-selection/assets/img/avatar.png'
import { getUserInfo } from '@/api/user'
import { getToken } from '@/store/local/auth'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { DEFAULT_AVATAR } from '@/constants/avatar'

export default {
  name: 'ChatInterface',
  props: {
    selectedMbti: {
      type: String,
      default: ''
    },
    // 外部传入的简历 URL 字段，用于展示"当前简历"
    resumeUrl: {
      type: String,
      default: ''
    },
    // 会话 ID
    sessionId: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    MbtiHotJobCard,
    MbtiSelectionContent,
    UploadContent
  },
  emits: ['back', 'favorite-changed'],
  watch: {
    // 监听 sessionId 变化，重新加载会话详情
    sessionId: {
      immediate: true,
      handler(newSessionId) {
        if (newSessionId) {
          // 重置消息分页状态
          this.messagesPage = 1
          this.messagesTotal = 0
          this.messagesHasMore = false
          this.messages = []
          // 重置会话详情数据
          this.sessionMbtiType = ''
          this.sessionResumeUrl = ''
          // 先获取会话详情
          this.fetchSessionDetail()
          // 再加载历史消息
          this.loadSessionDetail(false)
        } else {
          this.messages = []
          this.sessionMbtiType = ''
          this.sessionResumeUrl = ''
        }
      }
    }
  },
  data() {
    return {
      inputText: '',
      // 上传相关状态
      isUploading: false,
      // 流式状态
      isStreaming: false,
      // 语音录制 / 语音转文字状态
      isRecording: false,
      mediaRecorder: null,
      recordedChunks: [],
      speechRecognition: null,
      voiceText: '',
      uploadAccept: '.doc,.docx,.pdf,.png,.jpg,.jpeg',
       // MBTI 相关
      mbtiDialogVisible: false,
      currentMbti: this.selectedMbti || '',
      // 从会话详情获取的 MBTI 和简历 URL
      sessionMbtiType: '',
      sessionResumeUrl: '',
      // 简历上传弹框
      resumeDialogVisible: false,
      tempResumeUrl: '',
      defaultAvatar: aiAvatar,
      messages: [],
      // 消息分页
      messagesPage: 1,
      messagesPageSize: 20,
      messagesTotal: 0,
      messagesHasMore: false,
      messagesLoading: false,
      // 存储每个消息的渲染后的 markdown HTML（用于防抖）
      renderedMarkdownCache: {},
      // 收藏相关
      isFavorite: false,
      favoriteActiveIcon: favoriteActiveIconUrl,
      favoriteDefaultIcon: favoriteDefaultIconUrl,
      voiceIcon: voiceIcon,
      voiceGif: voiceGif
    }
  },
  computed: {
    // 顶部展示的 MBTI，优先用会话详情，其次用当前选择，最后用 props
    displayMbti() {
      return this.sessionMbtiType || this.currentMbti || this.selectedMbti
    },
    // 展示的简历 URL，优先用会话详情，其次用 props
    displayResumeUrl() {
      return this.sessionResumeUrl || this.resumeUrl
    },
    // 从 URL 中提取文件名
    displayResumeFileName() {
      const url = this.displayResumeUrl
      if (!url) {
        return '未上传'
      }
      try {
        // 从 URL 中提取文件名
        // 处理完整 URL 或相对路径
        const urlObj = new URL(url, window.location.origin)
        const pathname = urlObj.pathname
        let fileName = pathname.split('/').pop() || pathname
        // 如果文件名不存在，尝试从完整 URL 中提取
        if (!fileName || fileName === pathname) {
          fileName = url.split('/').pop() || ''
        }
        // 去掉查询参数
        if (fileName) {
          fileName = fileName.split('?')[0]
          // 解码 URL 编码的中文字符（UTF-8）
          try {
            fileName = decodeURIComponent(fileName)
          } catch (decodeError) {
            // 如果解码失败，使用原始文件名
            console.warn('文件名解码失败:', decodeError)
          }
        }
        return fileName || '未上传'
      } catch (e) {
        // 如果不是有效的 URL，尝试直接提取路径的最后一部分
        const parts = url.split('/')
        let fileName = parts[parts.length - 1]
        // 如果包含查询参数，去掉查询参数
        if (fileName) {
          fileName = fileName.split('?')[0]
          // 解码 URL 编码的中文字符（UTF-8）
          try {
            fileName = decodeURIComponent(fileName)
          } catch (decodeError) {
            // 如果解码失败，使用原始文件名
            console.warn('文件名解码失败:', decodeError)
          }
        }
        return fileName || '未上传'
      }
    },
    userAvatar() {
      let avatar = null
      
      // 优先从 store 读取
      if (this.$store.state.user?.avatar) {
        avatar = this.$store.state.user.avatar
      } else {
        try {
          const userInfo = localStorage.getItem('userInfo')
          if (userInfo) {
            const info = JSON.parse(userInfo)
            if (info.avatar) {
              avatar = info.avatar
            }
          }
        } catch (e) {
          console.warn('解析用户信息失败:', e)
        }
      }
      
      if (!avatar || !avatar.includes('https')) {
        return DEFAULT_AVATAR
      }
      
      return avatar
    }
  },
  created() {
    // 初始化时尝试获取用户信息
    this.initUserInfo()
  },
  methods: {
    /**
     * 初始化用户信息：从 localStorage 读取或调用 API 获取
     */
    async initUserInfo() {
      const token = getToken()
      if (!token) {
        return
      }

      // 先尝试从 localStorage 读取
      try {
        const userInfo = localStorage.getItem('userInfo')
        if (userInfo) {
          const info = JSON.parse(userInfo)
          if (info.avatar) {
            this.$store.commit('user/SET_AVATAR', info.avatar)
            return
          }
        }
      } catch (e) {
        console.warn('解析用户信息失败:', e)
      }

      // 如果 localStorage 没有，且 store 也没有，则调用 API
      if (!this.$store.state.user?.avatar) {
        try {
          const response = await getUserInfo()
          if (response.data.code === 1 && response.data.data) {
            const userData = response.data.data
            const avatar = userData.avatar
            if (avatar) {
              this.$store.commit('user/SET_AVATAR', avatar)
            }
          }
        } catch (error) {
          console.error('获取用户信息失败:', error)
        }
      }
    },
    getMessageRoleClass(msg) {
      if (msg && typeof msg.sender_role !== 'undefined') {
        return msg.sender_role === 1 ? 'user' : 'assistant'
      }
      // 兼容已有的 role 字段（'user' / 'assistant'）
      if (msg && msg.role === 'user') return 'user'
      if (msg && msg.role === 'assistant') return 'assistant'
      return 'assistant'
    },

    /**
     * 获取消息类型，默认文本(1)
     */
    getMessageType(msg) {
      if (!msg) return 1
      return msg.message_type || msg.messageType || 1
    },

    /**
     * 预处理流式 markdown 内容，避免在流式输出时误解析标题等元素
     */
    preprocessStreamingContent(content, isStreaming) {
      if (!content || !isStreaming) return content
      
      const lines = content.split('\n')
      if (lines.length > 0) {
        const lastLine = lines[lines.length - 1]
        
        const titleMatch = lastLine.match(/^(#+)\s*(.*)$/)
        if (titleMatch) {
          const titleContent = titleMatch[2] || ''
          
          if (titleContent.length < 8 || !titleContent.trim()) {
            lines.pop()
            return lines.join('\n')
          }
        }
      }
      
      return content
    },

    renderMarkdown(content, isStreaming = false) {
      if (!content) return ''
      try {
        const processedContent = isStreaming ? this.preprocessStreamingContent(content, isStreaming) : content
        const html = marked.parse(processedContent)
        const cleanHtml = DOMPurify.sanitize(html, {
          ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', 'dl', 'dt', 'dd',
            'blockquote', 'pre', 'code',
            'a', 'img',
            'table', 'thead', 'tbody', 'tr', 'th', 'td',
            'hr', 'div', 'span'
          ],
          ALLOWED_ATTR: [
            'href', 'title', 'alt', 'src', 'width', 'height',
            'class', 'id', 'target', 'rel'
          ],
          ALLOW_DATA_ATTR: false,
          // 不允许在 href 中使用 javascript: 协议
          FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
          FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
        })
        return cleanHtml
      } catch (error) {
        console.error('Markdown渲染失败:', error)
        // 即使解析失败，也对原始内容进行 XSS 过滤
        return DOMPurify.sanitize(content)
      }
    },

    getRenderedMarkdown(msg) {
      if (!msg) return ''
      const msgId = msg.id || msg.msg_id || `${msg.sender_role}_${Date.now()}`
      const content = msg.content || ''
      
      if (msg.streaming) {
        const lastContent = this.renderedMarkdownCache[`${msgId}_content`]
        if (lastContent === content && this.renderedMarkdownCache[msgId]) {
          return this.renderedMarkdownCache[msgId]
        }
        
        const rendered = this.renderMarkdown(content, true) // 传递 isStreaming=true
        // 更新缓存
        this.$set(this.renderedMarkdownCache, `${msgId}_content`, content)
        this.$set(this.renderedMarkdownCache, msgId, rendered)
        return rendered
      } else {
        // 非流式输出，使用缓存或直接渲染
        const lastContent = this.renderedMarkdownCache[`${msgId}_content`]
        if (lastContent === content && this.renderedMarkdownCache[msgId]) {
          return this.renderedMarkdownCache[msgId]
        }
        
        const rendered = this.renderMarkdown(content)
        this.$set(this.renderedMarkdownCache, msgId, rendered)
        this.$set(this.renderedMarkdownCache, `${msgId}_content`, content)
        return rendered
      }
    },

    /**
     * 获取图片地址
     */
    getImageSrc(msg) {
      return (
        msg.content ||
        ''
      )
    },

    /**
     * 获取文件地址
     */
    getFileUrl(msg) {
      return msg.content || ''
    },

    /**
     * 获取文件名
     */
    getFileName(msg) {
      return msg.fileName || msg.file_name || msg.filename || ''
    },

    async handleSend() {
      const text = this.inputText.trim()
      if (!text || this.isStreaming) return

      // 添加用户消息
      const userMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        sender_role: 1,
        message_type: 1,
        content: text,
        streaming: false,
        timestamp: Date.now()
      }
      this.messages.push(userMessage)
      this.inputText = ''

      // 滚动到底部显示新消息
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })

      // 添加 AI 回复占位（初始为空）
      const aiMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
        sender_role: 2,
        message_type: 1,
        content: '',
        streaming: true,
        timestamp: Date.now(),
        recommend_jobs: null // 初始化推荐岗位字段
      }
      this.messages.push(aiMessage)
      
      // 滚动到底部显示 AI 消息占位
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })

      // 开始流式接收
      this.isStreaming = true
      await this.streamResponse(text, aiMessage)
      this.isStreaming = false
    },
    handleHotJobClick(job) {
      const jobId = job.id || job.job_id
      if (!jobId) return
      // 跳转到热门岗位页面，使用 jobIds 参数调用 /api/search/jobsBylds 接口
      // 传递字符串形式的 jobIds，路由处理逻辑会将其分割为数组
      this.$router.push({
        path: '/hot-jobs',
        query: { jobIds: String(jobId) }
      })
    },

    // ====== 转发和收藏 ======
    /**
     * 转发
     */
    handleShare() {
      // TODO: 实现转发功能
      this.$message.info('转发功能开发中')
    },

    /**
     * 切换收藏状态
     */
    async handleToggleFavorite() {
      if (!this.sessionId) {
        this.$message.warning('当前没有可收藏的会话')
        return
      }

      const targetFavorite = !this.isFavorite

      try {
        await favoriteSession(this.sessionId, { favorite: targetFavorite} )
        this.isFavorite = targetFavorite

        // 通知父组件更新“我的收藏”列表
        this.$emit('favorite-changed', {
          sessionId: this.sessionId,
          favorite: this.isFavorite
        })

        if (this.isFavorite) {
          this.$message.success('收藏成功')
        } else {
          this.$message.success('取消收藏成功')
        }
      } catch (error) {
        console.error('更新收藏状态失败:', error)
        this.$message.error(error?.response?.data?.msg || '更新收藏状态失败')
      }
    },

    // ====== 会话相关接口 ======
    async streamResponse(content, aiMessage, messageType = 1) {
      try {
        const sessionId = this.sessionId
        await streamChat(
          { content: content, message_type: messageType },
          sessionId,
          {
            apiUrl: `/api/careerPlan/session/${sessionId}/chat`,
            onChunk: (chunk, fullText) => {
              const safeFullText = fullText || ''
              this.$set(aiMessage, 'content', safeFullText)
              
              // 滚动到底部显示最新内容
              this.$nextTick(() => {
                const container = this.$refs.messagesContainer
                if (container) {
                  container.scrollTop = container.scrollHeight
                }
              })
            },
            onComplete: (fullText, doneData) => {
              // 流完成
              const safeFullText = fullText || ''
              this.$set(aiMessage, 'content', safeFullText)
              this.$set(aiMessage, 'streaming', false)
              
              if (doneData) {
                
                const jobs = doneData.recommend_jobs
                console.log('jobs', jobs)
                
                if (jobs && Array.isArray(jobs) && jobs.length > 0) {
                  this.$set(aiMessage, 'recommend_jobs', jobs)
                  console.log('jobs2', jobs)
                  this.$nextTick(() => {
                    this.$forceUpdate()
                    // 滚动到底部显示推荐岗位
                    const container = this.$refs.messagesContainer
                    if (container) {
                      container.scrollTop = container.scrollHeight
                    }
                  })
                } else {
                  if (aiMessage.recommend_jobs) {
                    this.$set(aiMessage, 'recommend_jobs', null)
                  }
                }
              }
              
              // 滚动到底部
              this.$nextTick(() => {
                const container = this.$refs.messagesContainer
                if (container) {
                  container.scrollTop = container.scrollHeight
                }
              })
            },
            onError: (error) => {
              console.error('Stream error:', error)
              const errorText = (aiMessage.content || '') + '\n\n[错误: ' + error.message + ']'
              this.$set(aiMessage, 'content', errorText)
              this.$set(aiMessage, 'streaming', false)
              this.$message.error('消息发送失败，请重试')
            }
          }
        )
      } catch (error) {
        console.error('Stream chat failed:', error)
        const errorText = (aiMessage.content || '') + '\n\n[错误: 连接中断]'
        this.$set(aiMessage, 'content', errorText)
        this.$set(aiMessage, 'streaming', false)
        this.$message.error('消息发送失败，请重试')
      }
    },

    /**
     * 获取会话详情
     */
    async fetchSessionDetail() {
      if (!this.sessionId) {
        console.warn('sessionId 为空，无法获取会话详情')
        return
      }

      try {
        const response = await getSessionDetail(this.sessionId)
        // 处理会话详情数据
        const sessionDetail = response?.data?.data || response?.data
        
        // 保存会话详情中的 mbti_type 和 resume_url
        if (sessionDetail) {
          this.sessionMbtiType = sessionDetail.mbti_type || ''
          this.sessionResumeUrl = sessionDetail.resume_url || ''
        }
      } catch (error) {
        console.error('获取会话详情失败:', error)
        this.$message.error(error?.response?.data?.msg || '获取会话详情失败')
      }
    },

    async loadSessionDetail(loadMore = false) {
      if (!this.sessionId) {
        console.warn('sessionId 为空，无法加载历史消息')
        return
      }
      if (this.messagesLoading) return

      this.messagesLoading = true
      const container = this.$refs.messagesContainer
      const prevScrollHeight = container ? container.scrollHeight : 0

      try {
        // 非 loadMore 时重置分页为第1页（最新消息）
        if (!loadMore) {
          this.messagesPage = 1
        }

        const response = await getSessionMessages(this.sessionId, {
          page_no: this.messagesPage,
          page_size: this.messagesPageSize
        })
        
        // 根据后端返回结构处理数据
        const result = response?.data?.data || response?.data
        const list = result?.lists || result?.list || []
        
        // 确保每条消息都有唯一的 id
        const processedList = list.map((msg, idx) => {
          if (!msg.id && !msg.message_id) {
            // 如果没有 id，生成一个唯一的 id
            msg.id = `msg_${msg.timestamp || msg.create_time || Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`
          }
          return msg
        })

        if (loadMore) {
          // 例如：接口返回 [消息3, 消息2, 消息1]，反转后 [消息1, 消息2, 消息3]
          const reversedList = [...processedList].reverse()
          this.messages = [...reversedList, ...this.messages]

          this.$nextTick(() => {
            const c = this.$refs.messagesContainer
            if (c) {
              const newScrollHeight = c.scrollHeight
              // 保持视口位置不跳动：滚动位置 = 新高度 - 旧高度
              c.scrollTop = newScrollHeight - prevScrollHeight
            }
          })
        } else {
          const reversedList = [...processedList].reverse()
          this.messages = reversedList
          this.$nextTick(() => {
            const c = this.$refs.messagesContainer
            if (c) {
              // 滚动到底部，显示最新消息
              c.scrollTop = c.scrollHeight
            }
          })
        }

        this.messagesHasMore = Array.isArray(list) && list.length === this.messagesPageSize
      
        if (this.messagesHasMore) {
          this.messagesPage += 1
        }
      } catch (error) {
        console.error('加载历史消息失败:', error)
        this.$message.error(error?.response?.data?.msg || '加载历史消息失败')
      } finally {
        this.messagesLoading = false
      }
    },

    /**
     * 消息区域滚动事件：向上滚动触顶加载更多
     */
    handleMessagesScroll() {
      const container = this.$refs.messagesContainer
      if (!container) return
      if (this.messagesLoading || !this.messagesHasMore) return

      // 滚动到顶部附近时加载更多
      if (container.scrollTop <= 10) {
        this.loadSessionDetail(true)
      }
    },


    // ====== MBTI 修改弹框相关 ======
    handleMbtiSelect(code) {
      this.currentMbti = code || ''
    },
    async handleChangeMbti() {
      if (!this.currentMbti) {
        this.$message.warning('请先选择 MBTI 类型')
        return
      }
      if (!this.sessionId) {
        this.$message.error('会话 ID 不存在，无法更新')
        return
      }

      try {
        // 调用更新接口，更新 mbtiType
        await updateSession(this.sessionId, {
          mbtiType: this.currentMbti
        })
        
        // 更新本地状态
        this.sessionMbtiType = this.currentMbti
        
        this.$message.success('MBTI 类型更新成功')
        this.mbtiDialogVisible = false
      } catch (error) {
        console.error('更新 MBTI 类型失败:', error)
        this.$message.error(error?.response?.data?.msg || '更新 MBTI 类型失败，请重试')
      }
    },

    // 简历上传：接收 UploadContent 返回的 URL
    handleResumeUploaded(url) {
      this.tempResumeUrl = url || ''
      if (!this.tempResumeUrl) {
        console.warn('UploadContent 未返回有效的简历 URL', url)
      }
    },
    handleResumeCancel() {
      this.tempResumeUrl = ''
      this.resumeDialogVisible = false
    },
    async handleResumeConfirm() {
      if (!this.tempResumeUrl) {
        this.$message.warning('请先上传简历文件')
        return
      }
      if (!this.sessionId) {
        this.$message.error('会话 ID 不存在，无法更新')
        return
      }

      try {
        // 调用更新接口，更新 resumeUrl
        await updateSession(this.sessionId, {
          resume_url: this.tempResumeUrl
        })
        
        // 更新本地状态
        this.sessionResumeUrl = this.tempResumeUrl
        
        this.$message.success('简历更新成功')
        this.resumeDialogVisible = false
        this.tempResumeUrl = ''
      } catch (error) {
        console.error('更新简历失败:', error)
        this.$message.error(error?.response?.data?.msg || '更新简历失败，请重试')
      }
    },

    // // ====== 上传附件，接口与 UploadContent 一致 ======
    // handleBeforeUpload(file) {
    //   this.isUploading = true
    //   // 可以在这里做大小/类型校验（与 UploadContent 保持一致的话目前直接上传）
    //   const raw = file && (file.raw || file)
    //   const name = raw && raw.name ? decodeURIComponent(raw.name) : ''
    //   if (name) {
    //     this.$message.info(`开始上传附件：${name}`)
    //   }
    // },
    async handleUploadSuccess(response, file) {
      this.isUploading = false

      // 获取上传返回的URL
      const fileUrl = response?.data?.url || response?.data?.data?.url
      if (!fileUrl) {
        this.$message.error('上传成功，但未获取到文件URL')
        return
      }

      // 判断文件类型：根据文件后缀判断是否为图片（message_type: 2）或其他文件（message_type: 3）
      const raw = file && (file.raw || file)
      const originalFileName = raw && raw.name ? raw.name : '' // 保留原始文件名（用于显示）
      const fileName = originalFileName.toLowerCase() // 小写用于判断类型
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']
      const fileExtension = fileName.split('.').pop() || ''
      const isImage = imageExtensions.includes(fileExtension)
      const messageType = isImage ? 2 : 3

      const userMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        sender_role: 1,
        message_type: messageType, // 图片为2，其他文件为3
        content: fileUrl, // URL作为content
        fileName: originalFileName, // 保存原始文件名
        streaming: false,
        timestamp: Date.now()
      }
      this.messages.push(userMessage)

      // 滚动到底部显示新消息
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })

      // 添加 AI 回复占位（初始为空）
      const aiMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
        sender_role: 2,
        message_type: 1,
        content: '',
        streaming: true,
        timestamp: Date.now(),
        recommend_jobs: null // 初始化推荐岗位字段
      }
      this.messages.push(aiMessage)
      
      // 滚动到底部显示 AI 消息占位
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })

      // 开始流式接收AI回复
      this.isStreaming = true
      await this.streamResponse(fileUrl, aiMessage, messageType)
      this.isStreaming = false
    },
    handleUploadError() {
      this.isUploading = false
      this.$message.error('附件上传失败，请稍后重试')
    },

    // ====== 语音录制并转文字填入输入框 ======
    async handleVoiceClick() {
      try {
        if (!this.isRecording) {
          await this.startVoiceRecording()
        } else {
          await this.stopVoiceRecording()
        }
      } catch (error) {
        console.error('语音录制错误:', error)
        this.$message.error(error.message || '语音录制失败')
      }
    },
    async startVoiceRecording() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition

      if (!SpeechRecognition) {
        throw new Error('当前浏览器不支持语音转文字（SpeechRecognition）')
      }

      // 如果已有识别实例，先停止
      if (this.speechRecognition) {
        try {
          this.speechRecognition.abort()
        } catch (e) {
          console.error('中止已有语音识别实例失败:', e)
        }
        this.speechRecognition = null
      }

      const recognition = new SpeechRecognition()
      recognition.lang = 'zh-CN'
      recognition.interimResults = true
      recognition.continuous = false

      this.voiceText = ''

      recognition.onresult = (event) => {
        let text = ''
        for (let i = 0; i < event.results.length; i++) {
          text += event.results[i][0].transcript
        }
        this.voiceText = text
      }

      recognition.onerror = (event) => {
        console.error('语音识别错误:', event)
        this.isRecording = false
        this.speechRecognition = null
        this.$message.error('语音识别失败，请稍后重试')
      }

      recognition.onend = () => {
        // 识别自然结束
        this.isRecording = false
        this.speechRecognition = null

        if (this.voiceText) {
          // 将识别结果填入输入框（追加在当前内容之后）
          if (this.inputText && this.inputText.trim()) {
            this.inputText =
              this.inputText.replace(/\s+$/, '') + ' ' + this.voiceText
          } else {
            this.inputText = this.voiceText
          }
        } else {
          this.$message.info('未识别到语音内容')
        }
      }

      this.speechRecognition = recognition
      recognition.start()
      this.isRecording = true
      this.$message.info('开始语音输入，再次点击图标结束')
    },
    async stopVoiceRecording() {
      if (this.speechRecognition) {
        try {
          this.speechRecognition.stop()
        } catch (e) {
          console.error('停止语音识别失败:', e)
        }
      }
      this.isRecording = false
    },

    async handlePasteImage(event) {
      const clipboard = event.clipboardData || window.clipboardData
      if (!clipboard || !clipboard.items) return

      const items = clipboard.items
      let imageFile = null
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type && item.type.indexOf('image') === 0) {
          imageFile = item.getAsFile()
          break
        }
      }

      if (!imageFile) return

      // 阻止默认粘贴文本行为，避免在输入框里出现一堆 base64 文本
      event.preventDefault()

      try {
        await this.uploadBlobAsFile(
          imageFile,
          imageFile.name || `screenshot_${Date.now()}.png`,
          '截图'
        )
      } catch (error) {
        console.error('粘贴截图上传失败:', error)
        // uploadBlobAsFile 内部已有错误提示，这里不重复提示
      }
    },

    // ====== 通用 Blob 上传 ======
    async uploadBlobAsFile(blob, filename, displayName) {
      this.isUploading = true
      try {
        const formData = new FormData()
        // 与 UploadContent 的 el-upload 保持一致，字段名使用 file
        const file = new File([blob], filename, { type: blob.type })
        formData.append('file', file)

        const response = await fetch('/api/upload/imageObs', {
          method: 'POST',
          body: formData
        })

        if (!response.ok) {
          throw new Error(`上传失败：${response.statusText}`)
        }

        const result = await response.json()
        // 获取上传返回的URL
        const fileUrl = result?.data?.url || result?.data?.data?.url
        if (!fileUrl) {
          throw new Error('上传成功，但未获取到文件URL')
        }

        this.$message.success(`${displayName}上传成功`)

        // 粘贴的图片都是图片类型，message_type: 2
        const userMessage = {
          id: `user_${Date.now()}`,
          role: 'user',
          sender_role: 1,
          message_type: 2, // 图片消息
          content: fileUrl, // URL作为content
          fileName: filename, // 保存文件名
          streaming: false,
          timestamp: Date.now()
        }
        this.messages.push(userMessage)

        // 滚动到底部显示新消息
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer
          if (container) {
            container.scrollTop = container.scrollHeight
          }
        })

        // 添加 AI 回复占位（初始为空）
        const aiMessage = {
          id: `ai_${Date.now()}`,
          role: 'assistant',
          sender_role: 2,
          message_type: 1,
          content: '',
          streaming: true,
          timestamp: Date.now(),
          recommend_jobs: null // 初始化推荐岗位字段
        }
        this.messages.push(aiMessage)
        
        // 滚动到底部显示 AI 消息占位
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer
          if (container) {
            container.scrollTop = container.scrollHeight
          }
        })

        // 开始流式接收AI回复
        this.isStreaming = true
        await this.streamResponse(fileUrl, aiMessage, 2)
        this.isStreaming = false
      } catch (error) {
        console.error(`${displayName}上传失败:`, error)
        this.$message.error(`${displayName}上传失败，请稍后重试`)
      } finally {
        this.isUploading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.btn-base {
  padding: 0 24px;
  height: 36px;
  line-height: 36px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-interface {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-header {
  box-sizing: border-box;
  padding: 8px 150px;
  background: #ffffff;
  position: absolute;
  top:0;
  left: 0;
  width:100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-header-main {
  display: flex;
  flex-direction: column;
}

.chat-header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

.action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &:hover {
    background: #f5f5f5;
  }
}

.favorite-icon {
  &.active {
    transform: scale(1.1);
  }
}

.switch-btn {
  display: inline-flex;
  align-items: stretch;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(125, 106, 255, 0.18);
  font-size: 14px;
  margin-bottom:16px;
  margin-right: 16px;
}

.switch-btn-left {
  padding: 8px 16px;
  background: #ffffff;
  color: #64748b;
}
.el-dialog .mbti-grid{
  margin-bottom: 0px;
}

.mbti-code {
  font-weight: 600;
  color: #6366f1;
}

.switch-btn-btn {
  padding: 0 20px;
  border: none;
  outline: none;
  background: linear-gradient(90deg, #7EB4EB 0%, #B16EF9 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.switch-btn-btn:hover {
  opacity: 0.9;
}


.chat-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.chat-subtitle {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.chat-body {
  flex: 1;
  padding: 0 150px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 60px;
  .message-avatar {
    width: 40px;
    height: 40px;
    margin-right: 4px;

    img {
      width: 100%;
      height: 100%;
    }
  }
}

.chat-message {
  display: flex;
  margin-bottom: 12px;
  width: 100%;
  min-width: 0;
}

.chat-message.user {
  justify-content: flex-end;
}

.chat-message.assistant {
  justify-content: flex-start;
}

.chat-bubble {
  width: auto;
  max-width: 95%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  
  // 确保 markdown-content 容器本身没有额外的 margin 和 padding
  .markdown-content {
    margin: 0;
    padding: 0;
    display: block;
    // 确保基础字体大小固定，避免突然变化
    font-size: 14px;
    line-height: 1.6;
    
    // markdown 渲染后的根元素如果是 div，移除其 margin
    > div:last-child {
      margin-bottom: 0;
    }
  }
}
.msg-image {
  max-width: 200px;
  height: auto;
  border-radius: 8px;
  display: block;
}

.msg-file {
  display: inline-block;
  color: #6366f1;
  text-decoration: none;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: transparent;
  transition: all 0.2s;
}

.msg-file:hover {
  background: #f3f4f6;
  border-color: #6366f1;
}

.chat-message.user .chat-bubble {
  background: linear-gradient(90deg, #7EB4EB 0%, #B16EF9 100%);
  color: #fff;
  border-bottom-right-radius: 2px;
  max-width: none;
  width: fit-content;
  word-wrap: normal;
  word-break: normal;
  white-space: normal;
}

.chat-message.user .chat-message-content {
  max-width: none;
  width: fit-content;
}

.chat-message.assistant .chat-bubble {
  background: #fff;
  color: #333;
}

/* Markdown 样式支持 - 使用 ::v-deep 穿透 scoped 样式 */
.chat-bubble ::v-deep .markdown-content h1,
.chat-bubble ::v-deep .markdown-content h2,
.chat-bubble ::v-deep .markdown-content h3,
.chat-bubble ::v-deep .markdown-content h4,
.chat-bubble ::v-deep .markdown-content h5,
.chat-bubble ::v-deep .markdown-content h6 {
  margin: 12px 0 6px 0;
  font-weight: 600;
  line-height: 1.4;
  color: #333;
}

.chat-bubble ::v-deep .markdown-content h1 { font-size: 22px; }
.chat-bubble ::v-deep .markdown-content h2 { font-size: 20px; }
.chat-bubble ::v-deep .markdown-content h3 { font-size: 18px; }
.chat-bubble ::v-deep .markdown-content h4 { font-size: 16px; }
.chat-bubble ::v-deep .markdown-content h5 { font-size: 15px; }
.chat-bubble ::v-deep .markdown-content h6 { font-size: 14px; }

.chat-bubble ::v-deep .markdown-content p {
  margin: 0;
  line-height: 1.5;
  color: #333;
}

// 移除 markdown-content 内部最后一个元素的底部 margin
.chat-bubble ::v-deep .markdown-content > *:last-child {
  margin-bottom: 0 !important;
}

.chat-bubble ::v-deep .markdown-content ul,
.chat-bubble ::v-deep .markdown-content ol {
  margin: 6px 0;
  padding-left: 24px;
  padding-top: 0;
  padding-bottom: 0;
}

.chat-bubble ::v-deep .markdown-content li {
  margin: 0;
  line-height: 1.5;
  color: #333;
}

.chat-bubble ::v-deep .markdown-content ul {
  list-style-type: disc;
}

.chat-bubble ::v-deep .markdown-content ol {
  list-style-type: decimal;
}

.chat-bubble ::v-deep .markdown-content code {
  background: rgba(0, 0, 0, 0.08);
  padding: 3px 8px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 0.9em;
  color: #e83e8c;
}

.chat-bubble ::v-deep .markdown-content pre {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 10px 0;
  border: 1px solid #e9ecef;
}

.chat-bubble ::v-deep .markdown-content pre code {
  background: none;
  padding: 0;
  color: #333;
  font-size: 0.9em;
}

.chat-bubble ::v-deep .markdown-content blockquote {
  border-left: 4px solid #7EB4EB;
  padding-left: 16px;
  margin: 10px 0;
  color: #666;
  font-style: italic;
  background: rgba(126, 180, 235, 0.05);
  padding-top: 8px;
  padding-bottom: 8px;
  padding-right: 12px;
  border-radius: 4px;
}

.chat-bubble ::v-deep .markdown-content strong {
  font-weight: 600;
  color: #333;
}

.chat-bubble ::v-deep .markdown-content em {
  font-style: italic;
}

.chat-bubble ::v-deep .markdown-content a {
  color: #7EB4EB;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all 0.2s;
}

.chat-bubble ::v-deep .markdown-content a:hover {
  color: #5a9de8;
  border-bottom-color: #5a9de8;
}

.chat-bubble ::v-deep .markdown-content hr {
  border: none;
  border-top: 1px solid #e9ecef;
  margin: 12px 0;
}

.chat-bubble ::v-deep .markdown-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

.chat-bubble ::v-deep .markdown-content th,
.chat-bubble ::v-deep .markdown-content td {
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  text-align: left;
}

.chat-bubble ::v-deep .markdown-content th {
  background: #f8f9fa;
  font-weight: 600;
}

// 消息加载动画
.message-loading {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

.loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #9ca3af;
  animation: loading-bounce 1.4s infinite ease-in-out;
}

.loading-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dot:nth-child(2) {
  animation-delay: -0.16s;
}

.loading-dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chat-footer {
  padding: 16px 150px 0;
  box-sizing: border-box;
}

.chat-input-wrapper {
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.4);
  padding: 8px 20px;
  display: flex;
  flex-direction: column;
}

.chat-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  font-size: 14px;
  line-height: 1.6;
  color: #111827;
}

.chat-input::placeholder {
  color: #9ca3af;
}

.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
}

.chat-message-content {
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.hot-jobs-wrapper {
  width: 95%;
  max-width: 95%;
  box-sizing: border-box;
  margin-top: 8px;
}

.hot-jobs-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  min-width: 0;
}
.upload-btn {
  @extend .btn-base;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  background: #f9fafb;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  color: #374151;
}

.upload-btn:hover {
  background: #f3f4f6;
}

.upload-icon {
  font-size: 16px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  @extend .btn-base;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  font-size: 16px;
}

.icon-btn:hover {
  background: #f3f4f6;
}

.icon-btn-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.btn-send {
  @extend .btn-base;
  padding: 0 20px;
  background: linear-gradient(90deg, #7EB4EB 0%, #B16EF9 100%);
  border: none;
  color: #fff;
  font-weight: 500;
  border-radius: 999px;
}

.btn-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

// MBTI 弹框底部按钮区域样式，增加与内容的分割感
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

// MBTI 弹框标题样式
:deep(.el-dialog__header .el-dialog__title) {
  font-size: 26px;
  font-weight: 800;
}

// 简历弹框 header 样式
:deep(.resume-dialog .el-dialog__header) {
  padding: 0px 20px;
}

// 收缩状态下增加左右padding
.main-content.sidebar-collapsed .chat-header,
.main-content.sidebar-collapsed .chat-body,
.main-content.sidebar-collapsed .chat-footer {
  padding-left: 260px;
  padding-right: 260px;
}
</style>

